import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';
import { categoryBadgeClass, formatViews } from '@/utils/format';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'wide';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'wide') {
    return (
      <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:flex-row">
        <Link to={`/article/${article.slug}`} className="block sm:w-[220px] sm:shrink-0">
          <div className="relative h-44 w-full overflow-hidden sm:h-full">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 美日吃瓜`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex flex-1 flex-col justify-center p-5">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <Link
              to={`/category/${article.category}`}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${categoryBadgeClass(article.category)}`}
            >
              {article.categoryName}
            </Link>
            <span className="text-xs text-foreground-500">{article.date}</span>
          </div>
          <h3 className="font-heading text-lg font-semibold leading-snug text-foreground-950">
            <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="line-clamp-2 mt-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.slug}`} className="block">
        <div className="relative h-48 w-full overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 美日吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span
            className={`absolute left-3 top-3 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur ${categoryBadgeClass(article.category)}`}
          >
            {article.categoryName}
          </span>
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="line-clamp-2 mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}